import { LEG_MARKET_IDS, MARKETS } from './registry.js';
/** Is a market eligible for a league given its coverage? */
function isEligible(m, coverage) {
    if (m.requires === 'events')
        return coverage.events === true;
    if (m.requires === 'stats')
        return coverage.stats === true;
    return true; // score + halftime are always available
}
/**
 * The leg catalog a fixture offers — every eligible leg market for the league,
 * filtered to what its provider coverage can grade, in display order. Written onto
 * the Fixture at ingest. The COVERAGE-gated part (events/stats) is frozen once the
 * round is in flight so a later coverage change can't rewrite a live round; the
 * always-on (score/half-time) part is implied for every fixture (see `isLegAllowed`).
 * The player builds their card by choosing any subset (the 1X2 anchor is always
 * implied and never in the list).
 */
export function legCatalogForFixture(coverage = {}) {
    return LEG_MARKET_IDS.filter((id) => isEligible(MARKETS[id], coverage));
}
/** Does a market depend on per-league provider coverage (the goal/card timeline or
 *  `/fixtures/statistics`)? Such legs are only valid where the league enabled them.
 *  Score/half-time markets are pure goal-maths and need no coverage. */
function isCoverageGated(m) {
    return m?.requires === 'events' || m?.requires === 'stats';
}
/**
 * Is a market eligible to be stacked as a LEG on a fixture, given that fixture's
 * frozen `legCatalog`? The single rule shared by the bet-builder (what to show),
 * `validateCard`, and the `submitEntry` coercer — so the client never offers a leg
 * the server would reject, and vice-versa.
 *
 * - ALWAYS-ON legs (score/half-time) are eligible for EVERY fixture, full stop. They
 *   never need to appear in the frozen catalog, so a newly-added always-on boost
 *   (e.g. a new half-time market) lights up on rounds already in flight — no backfill.
 * - COVERAGE-gated legs (events/stats) are eligible only when present in the frozen
 *   catalog: that membership is the per-league coverage proof captured at ingest.
 *   With no catalog (unknown), coverage legs are disallowed.
 */
export function isLegAllowed(marketId, legCatalog) {
    const m = MARKETS[marketId];
    if (!m || m.anchor)
        return false;
    if (isCoverageGated(m))
        return !!legCatalog && legCatalog.includes(marketId);
    return true;
}
/**
 * Merge a fixture's previously-frozen `legCatalog` with a freshly-computed one,
 * preserving canonical display order and never dropping a leg (a player may already
 * have backed it). Used by ingest to UNION newly-eligible legs into a round that's
 * still accepting picks, so a market/coverage addition surfaces pre-kickoff. Unknown
 * or retired ids are filtered out by virtue of `LEG_MARKET_IDS` membership.
 */
export function mergeLegCatalog(prev, fresh) {
    const keep = new Set([...(prev ?? []), ...(fresh ?? [])]);
    return LEG_MARKET_IDS.filter((id) => keep.has(id));
}
//# sourceMappingURL=coverage.js.map